/**
 * search-panel.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  animate,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  style,
  transition,
  trigger
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";

import { AlertService } from "../common/alert.service";
import { TypedData, TypedDataHolder } from "../common/data/typed-data";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { empty, valueless } from "../support/utilities/functions";

import { isInequality, Operator, OperatorString, MagicOperator, PastXDays } from "./operators";
import { FilterNode, PartitionNode, SearchNode, SearchTerm }
  from "./search-node";
import { SearchService } from "./search.service";

const ANIMATION_SPEED_IN : number = 300; // TODO: to the config
const ANIMATION_SPEED_OUT : number = 300; // TODO: to the config

/**
 * 
 */
@Component({
  selector: "search-panel",
  template: `
    <div [class.well]="!unwrap">
      <div *ngIf="!enforcedType" class="form-group well-top">
        <select [formControl]="contextType" class="form-control">
          <option
            *ngFor="let type of search.availableTypes"
            class="uppercase">
            {{type}}
          </option>
        </select>
        <hr>
      </div>

      <template [ngIf]="answered.length > 0">
        <h4>Query</h4>
      </template>
      <div
        *ngFor="let field of answered; let i = index"
        class="row fieldEntry"
        @fadeInOut>
        <div class="col-xs-12">
          <label>
            {{field.displayName | uppercase}}
            <em *ngIf="hasOpValue(field)">
              {{operatorLabel(field)}}
            </em>
          </label>
          <a class="pull-right text-danger" (click)="truncateTo(i)">Remove</a>
          <typed-control
            [input]="substituteMagic(field)"
            [control]="form.get(field.name)"
            [clearButton]="false">
          </typed-control>
        </div>
      </div>

      <template [ngIf]="partitions.length > 0">
        <hr *ngIf="answered.length > 0"/>
        <h4>Search Terms</h4>
      </template>
      <div
        *ngFor="let partition of partitions; let i = index"
        class="row fieldEntry searchTerm"
        @fadeInOut>
        <div class="col-xs-12" #term>
          <label>
            {{partition.displayName | uppercase}}
            <div
              *ngIf="allowsFreeTextOverride.has(partition.name)"
              class="pull-right">
              <button
                class="btn btn-default btn-xs"
                (click)="term.forceFreeText = !term.forceFreeText">
                Toggle free text
              </button>
            </div>
          </label>
          <typed-control
            [id]="'partition' + i"
            [input]="partition"
            [control]="form.get(partition.name)"
            [clearButton]="false"
            [disableRestrictions]="term.forceFreeText"
            (change)="supply(partition)"
            (keydown.enter)="supply(partition, true)">
          </typed-control>
        </div>
      </div>

      <template [ngIf]="filters.length > 0">
        <hr/>
        <h4>Filters</h4>
      </template>
      <div
        *ngFor="let filter of filters; let i = index"
        class="row fieldEntry"
        @fadeInOut>
        <div class="col-xs-12">
          <table width="100%">
            <tr>
              <th>
                {{filter.displayName | uppercase}}
              </th>
              <td>
                <select
                  *ngIf="filter.operators?.length > 1"
                  class="form-control input-sm"
                  [formControl]="opChoices.get(filter.name)">
                  <option
                    *ngFor="let op of filter.operators"
                    [ngValue]="op">
                    {{operatorLabel(filter, op)}}
                  </option>
                </select>
              </td>
            </tr>
          </table>
          <typed-control
            [id]="'filter' + i"
            [input]="substituteMagic(filter)"
            [control]="form.get(filter.name)"
            [clearButton]="false"
            (change)="supply(filter)"
            (keydown.enter)="supply(filter, true)">
          </typed-control>
        </div>
      </div>

      <table *ngIf="usingTerminalOperator" class="table center-items text-info">
        <tr>
          <td>
            <h1><span class="glyphicon glyphicon-info-sign"></span></h1>
          </td>
          <td>
            Tip: you can only add an "inexact" selection (such as greater than,
            less than, before, or after) as the last item in your search. If
            you wish to add other "exact" selections, please add them first.
          </td>
        </tr>
      </table>

      <div class="row">
        <div class="col-xs-6 form-group">
          <button
            class="btn btn-default"
            (click)="clear()">
            Clear Query
          </button>
        </div>
        <div class="col-xs-6 form-group">
          <button
            type="submit"
            class="btn btn-primary"
            (click)="submit()"
            [disabled]="!canSearch">
            Search
          </button>
        </div>
      </div>
    </div>

    <h2>
      Saved Searches
      <button
        class="btn-sm btn-success pull-right"
        [disabled]="!search.resultTime"
        (click)="search.saveSearch()">
        Save Last Query
      </button>
    </h2>
    <em *ngIf="search.savedSearches.length === 0">
      No saved searches found on this device.
    </em>
    <div *ngFor="let saved of search.savedSearches; let i = index" class="well">
      <table class="table table-sm">
        <tr class="text-info">
          <td>
            <button
              class="btn btn-primary btn-xs"
              (click)="search.runSaved(i)">
              <span class="glyphicon glyphicon-search"></span> Run
            </button>
          </td>
          <td>
            <button
              class="btn btn-danger btn-xs"
              (click)="search.deleteSaved(i)">
              <span class="glyphicon glyphicon-times"></span> Remove
            </button>
          </td>
          <th>
            {{saved.name}}
          </th>
        <tr>
          <th>type</th>
          <td>=</td>
          <td>{{saved.type}}</td>
        </tr>
        <tr *ngFor="let term of saved.terms">
          <th>{{term.name}}</th>
          <td>{{term.magicSource ? "≛" : term.operator}}</td>
          <td *ngIf="term.magicSource">
              {{term.magicSource[0]?.label}}
              ({{term.magicSource[1]}})
          </td>
          <td *ngIf="!term.magicSource">
            <span *ngIf="term?.value?.setTime">
              <!-- poor man's Date duck-typing TODO: replace -->
              {{term.value | utcDate}}
            </span>
            <span *ngIf="!term?.value?.setTime">{{term.value | json}}</span>
          </td>
        </tr>
      </table>
    </div>

    <h2 *ngIf="search.recentSearches.size() > 0">
      Recent Queries
      <button
        class="btn-sm btn-default pull-right"
        (click)="search.clearRecents()">
        Clear
      </button>
    </h2>
    <div
      *ngFor="let recent of search.recentSearches | reverse; let i = index"
      class="well">
      <table class="table table-sm">
        <tr class="text-info">
          <td>
            <button
              class="btn btn-primary btn-xs"
              (click)="runRecentQuery(i)">
              <span class="glyphicon glyphicon-search"></span> Re-Run
            </button>
          </td>
          <td></td>
          <th>
            {{recent[4] ? "More than" : ""}}
            {{recent[2] | plural:"result"}}
            <span class="pull-right">{{recent[3] | date:"mediumTime"}}</span>
          </th>
        <tr>
          <th>type</th>
          <td>=</td>
          <td>{{recent[0]}}</td>
        </tr>
        <tr *ngFor="let term of recent[1]">
          <th>{{term.name}}</th>
          <td>{{term.magicSource ? "≛" : term.operator}}</td>
          <td *ngIf="term.magicSource">
              {{term.magicSource[0]?.label}}
              ({{term.magicSource[1]}})
          </td>
          <td *ngIf="!term.magicSource">
            <span *ngIf="term?.value?.setTime">
              <!-- poor man's Date duck-typing TODO: replace -->
              {{term.value | utcDate}}
            </span>
            <span *ngIf="!term?.value?.setTime">{{term.value | json}}</span>
          </td>
        </tr>
      </table>
    </div>
`,
  styles: [
    `.fieldEntry {
      overflow: hidden;
    }
    .searchTerm label {
      width: 100%;
    }
    .fieldEntry >>> .form-group {
      margin-top: 0px;
    }
    .well {
      padding: 5px 19px;
    }
    h4 {
      font-style: italic;
    }
    .form-group > button {
      width: 100%;
    }
    .well-top {
      margin: auto -14px;
    }
    td>.btn:only-child {
      width: 100%;
    }`
  ],
  animations: [
    trigger("fadeInOut", [])
    //    [
    //     transition("void => *", [
    //       style({height: 0, opacity: 0}),
    //       animate(ANIMATION_SPEED_IN, style({height: "*", opacity: 1}))
    //     ]),
    //     transition("* => void", [
    //       style({height: "*", opacity: 1}),
    //       animate(ANIMATION_SPEED_OUT, style({height: 0, opacity: 0}))
    //     ])
    //   ])
  ]
})
export class SearchPanelComponent implements OnInit
{
  public form : FormGroup;
  public opChoices : FormGroup;
  public contextType : FormControl;
  public answered : SearchNode[] = [];
  public partitions : PartitionNode[] = [];
  public filters : FilterNode[] = [];
  private path : string[] = [];
  public canSearch : boolean = false;

  public allowsFreeTextOverride : Set<string> = new Set(["case handler"]);

  @Input()
  public enforcedType : SubjectContextType;

  @Input()
  public unwrap : boolean = false;

  @Output()
  public onSubmit : EventEmitter<[SubjectContextType, SearchTerm[]]> =
    new EventEmitter();

  public usingTerminalOperator : boolean = false;

  /**
   * Construct a new {@link SearchComponent}.
   *
   */
  public constructor (
    public search : SearchService,
    public alert : AlertService
  )
  {
    // Nothing
  }

  public ngOnInit () : void
  {
    this.form = new FormGroup({});
    this.opChoices = new FormGroup({});
    this.contextType = new FormControl();
    this.contextType.valueChanges.subscribe(() => {
      this.clear();
    });
    this.search.ready.subscribe((ready : boolean) => {
      if (ready)
      {
        this.contextType.setValue(this.search.availableTypes[0]);
        // the value setting will trigger clear() [see above]
      }
    });
  }

  private clear () : void
  {
    this.truncateTo(0);
  }

  private truncateTo (i : number) : void
  {
    if (i < this.path.length)
    {
      // if we are truncating anything, the terminal operator will be removed
      this.usingTerminalOperator = false;
    }
    this.path = this.path.slice(0, i);
    while (this.answered.length > i)
    {
      const removed : SearchNode = this.answered.pop();
      const control : AbstractControl = this.form.get(removed.name);
      control.enable();
      control.reset();
    }
    this.retrieveSearchFields();
  }

  private retrieveSearchFields () : void
  {
    const root : SearchNode = this.search.getRoot(this.getType());
    const parent : SearchNode = root.retrieve(this.path);
    if (!parent)
    {
      console.error(this.path, root);
      throw new Error("Provided search path is not valid");
    }
    this.canSearch = parent.canSearchHere();
    this.partitions = parent.paritions;
    this.filters = parent.filters;
    this.generateControls();
  }

  private generateControls () : void
  {
    this.partitions.forEach((node : SearchNode) => this.addControl(node));
    this.filters.forEach((node : SearchNode) => this.addControl(node));
    setTimeout(() => this.focusFirst(), 100);
  }

  private focusFirst () : void
  {
    const n : string = (this.partitions.length > 0) ? "partition0" : "filter0";
    const el : Element = document.querySelector(`#${n} .focusable`);
    if (el)
    {
    // annoying type mismatch between Element and HTMLElement I guess?
    (el as any).focus();
    }
  }

  private addControl (node : SearchNode) : void
  {
    if (!this.form.contains(node.name))
    {
      this.form.addControl(
        node.name,
        new FormControl()
      );
    }
    if (!this.opChoices.contains(node.name))
    {
      this.opChoices.addControl(
        node.name,
        new FormControl(node.operators[0])
      );
    }
    else if (node instanceof PartitionNode)
    {
      // We may have "leftover" controls from a filter node with the same name,
      // but since we aren't allowing selection of partition operator yet, we
      // set it to equality so that it isn't (invisibly) left as something else.
      this.opChoices.get(node.name).setValue(Operator.EQUALS);
    }
  }

  public supply (node : SearchNode, trySubmit : boolean = false) : void
  {
    if (empty(this.form.get(node.name).value))
    {
      // Don't add to the if the field's value is empty
      return;
    }
    const opField : AbstractControl = this.opChoices.get(node.name);
    if (opField && valueless(opField.value))
    {
      console.info(opField);
      // Don't add to query if it has an unchosen operator
      return;
    }
    if (this.path.indexOf(node.name) > -1)
    {
      // Don't add to query if it's already there
      return;
    }
    this.path.push(node.name);
    this.answered.push(node);
    const control = this.form.get(node.name);
    control.disable();

    if (opField && isInequality(opField.value))
    {
      // if the new field's operator is an inequality, flag it as "terminal" and
      // *don't* load more search fields
      this.usingTerminalOperator = true;
      this.partitions = [];
      this.filters = [];
    }
    else
    {
      this.retrieveSearchFields();
    }

    if (trySubmit && node.canSearchHere())
    {
      console.log("Attempting submit");
      this.submit();
    }
  }

  private getType () : SubjectContextType
  {
    return this.enforcedType || this.contextType.value;
  }

  public submit () : void
  {
    const terms : SearchTerm[] = this.path.map((name : string) => {

        const opField : {value : OperatorString | MagicOperator<any>} =
          this.opChoices.get(name);
        if (opField)
        {
          if (opField.value instanceof MagicOperator)
          {
            return opField.value.transform(name, this.form.get(name).value);
          }
          return {
            name,
            operator: opField.value,
            value: this.form.get(name).value
          };
        }
        else
        {
          return {
            name,
            operator: Operator.EQUALS,
            value: this.form.get(name).value
          };
        }
    });
    this.onSubmit.emit([this.getType(), terms]);
    this.search.runQuery(this.getType(), terms);
  }

  public substituteMagic (filter : FilterNode) : TypedDataHolder
  {
    const op : AbstractControl = this.opChoices.get(filter.name);
    if (op && op.value instanceof MagicOperator)
    {
      return {
        name: `${filter.name}-${op.value.name}`,
        type: op.value.argumentType,
        cardinality: 1, // only support scalars ATM
        value: null,
        restrictions: [],
        hints: {}
      };
    }
    return filter;
  }

  // public isMagical (filter : FilterNode) : boolean
  // {
  //   const op : AbstractControl = this.opChoices.get(filter.name);
  //   return (op && op.value instanceof MagicOperator);
  // }

  private hasOpValue (node : SearchNode) : boolean
  {
    return (!!this.opChoices.get(node.name));
  }

  private operatorLabel (node : SearchNode, op? : OperatorString) : string
  {
    if (!op)
    {
      if (!this.opChoices.get(node.name))
      {
        throw Error(`Could not find operator selection for ${node.name}`);
      }
      op = this.opChoices.get(node.name).value;
    }
    return Operator.labelFor(op, node);
  }

  public runRecentQuery (i : number) : void
  {
    // we're using the reversed array, so we need the reverse index
    this.search.runRecentQuery(this.search.recentSearches.size() - i - 1);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/search-panel.component.ts